import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { CustomerAction, CustomerFailureAction } from "@app/customer/store/actions";
import { BehaviorSubject, Subscription } from "rxjs";

import { CustomerAddComponent } from "@app/customer/components/customer-add/customer-add.component";
import { CustomerEditComponent } from "@app/customer/components/customer-edit/customer-edit.component";
import { routerGo } from "@app/root-store/actions";
import { UserAddComponent } from "@app/user/components/user-add/user-add.component";
import { UserEditComponent } from "@app/user/components/user-edit/user-edit.component";
import { User } from "@app/user/model/user";
import { UserActions } from "@app/user/store/actions";
import { Action } from "@ngrx/store";

@Component({
    selector: "clare-display-customer-info",
    templateUrl: "./display-customer-info.component.html",
    styleUrls: ["./display-customer-info.component.scss"],
})
export class DisplayCustomerInfoComponent implements OnInit, OnDestroy {
    @Input() isCustomer = true;
    @Input() customer: User;
    @Input() removeAction: "remove" | "delete" | "" = "delete";
    @Input() titleDescriptor = "Primary Customer";
    @Input() summaryTabSelected: boolean;
    @Input() customerInfoWidget = false;
    @Input() hideCancelBtn = false;
    @Input() editCustomerPage = false;
    /*
     * relayDialogEmitter
     * An emitter to used to relay updates from the inner component
     * up to the subscribing container component
     */
    @Output() relayDialogEmitter = new EventEmitter<{
        action: Action;
        closeDialog: EventEmitter<Action | void>;
    }>();
    /* */

    /*
     * closeDialog
     * An internal emitter subscribed to onInit which closes this.dialog when it emits
     * Should be passed up to and emitted by the container component
     */
    closeDialog = new EventEmitter<void>();
    /* */

    subscription: Subscription;

    private dialogRef: MatDialogRef<CustomerEditComponent | CustomerAddComponent | UserEditComponent | UserAddComponent>;

    constructor(private dialog: MatDialog) {}

    ngOnInit() {
        this.subscription = this.closeDialog.subscribe(() => {
            if (this.dialogRef) {
                this.dialogRef.close();
            }
        });
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    edit(customer: User = {} as User) {
        let component;
        if (this.isCustomer) {
            if (customer?.id) {
                component = CustomerEditComponent;
            } else {
                component = CustomerAddComponent;
            }
        } else if (customer?.id) {
            component = UserEditComponent;
        } else {
            component = UserAddComponent;
        }

        this.openAddEditCustomer(component, customer);
    }

    openAddEditCustomer(component, customer: User) {
        const dialogConfig: MatDialogConfig = {
            panelClass: "display-customer-modal", // [DDB-4666]: use the modal panel class to modify the ui for nested component
            autoFocus: true,
        };
        this.dialogRef = this.dialog.open(component, dialogConfig);

        this.relayInnerEmitter(this.dialogRef.componentInstance.emitter);

        if (this.dialogRef.componentInstance instanceof UserEditComponent) {
            this.dialogRef.componentInstance.user$ = new BehaviorSubject(customer);
        } else if (this.dialogRef.componentInstance instanceof CustomerEditComponent) {
            this.dialogRef.componentInstance.customer$ = new BehaviorSubject(customer);
            this.dialogRef.componentInstance.removeAction = this.removeAction;
            this.dialogRef.componentInstance.hideCancelBtn = this.hideCancelBtn;
            this.dialogRef.componentInstance.editCustomerPage = this.editCustomerPage;
        } else if (this.dialogRef.componentInstance instanceof CustomerAddComponent) {
            this.dialogRef.componentInstance.customer$ = new BehaviorSubject(customer);
        }
    }

    isCustomerFailureAction(action: Action): action is CustomerFailureAction {
        return (action as CustomerFailureAction).error?.requestData?.customer !== undefined;
    }

    isUserFailureAction(action: Action): action is UserActions.UserFailureAction {
        return (action as CustomerFailureAction).error?.requestData?.user !== undefined;
    }

    relayInnerEmitter(emitter: EventEmitter<Action>) {
        const emitterSubscription = emitter.subscribe((action: CustomerAction | Action) => {
            if (action) {
                if (action.type === routerGo.type) {
                    emitterSubscription.unsubscribe();
                    this.closeDialog.emit();
                } else if (!this.isUserFailureAction(action) && !this.isCustomerFailureAction(action)) {
                    this.relayDialogEmitter.emit({ action, closeDialog: this.closeDialog });
                    emitterSubscription.unsubscribe();
                }
            }
        });
    }
}
